import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Award, BookOpenCheck, ClipboardList, Download, FileText, MessageCircle, Quote, Scale } from "lucide-react";
import { whatsapp } from "@/data/site";
import { programmes } from "@/data/programmes";

export default function CoursePage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const p = programmes[key];
  if (!p) return <Navigate to="/" replace />;
  const enrol = `${whatsapp}%20I%20am%20interested%20in%20the%20${encodeURIComponent(p.title)}%20programme.`;
  const other = programmes[p.key === "diploma" ? "corporate" : "diploma"];
  const back = () => {
    navigate("/");
    setTimeout(() => document.getElementById("courses")?.scrollIntoView({ behavior: "smooth" }), 400);
  };

  return (
    <main data-testid="course-page">
      <section className="course-hero">
        <div className="course-hero-inner">
          <div className="course-hero-copy">
            <button className="back-link light" data-testid="back-to-programmes-link" onClick={back}><ArrowLeft size={13} /> All programmes</button>
            <div className="eyebrow gold">{p.type} · {p.tagline}</div>
            <h1 data-testid="course-page-title">{p.title}</h1>
            <p className="course-lead">{p.lead}</p>
            <div className="course-hero-actions">
              <a className="whatsapp-btn" data-testid="course-page-enrol-button" href={enrol} target="_blank" rel="noreferrer">Enrol via WhatsApp <MessageCircle size={17} /></a>
              <a className="brochure-btn" data-testid="course-page-brochure-button" href={p.brochure} target="_blank" rel="noreferrer"><Download size={16} /> Download brochure</a>
            </div>
          </div>
          <div className="course-hero-visual">
            <img src={p.image} alt={p.title} />
            <div className="image-stamp"><Scale size={18} /><span>{p.stamp[0]}<br /><b>{p.stamp[1]}</b></span></div>
          </div>
        </div>
      </section>

      {p.story && (
        <section className="course-story section-pad-slim">
          <p className="story-intro">{p.story.intro}</p>
          <div className="story-quotes">
            {p.story.quotes.map(q => (
              <blockquote key={q.text}><Quote size={18} /><p>“{q.text}”</p><cite>— {q.by}</cite></blockquote>
            ))}
          </div>
        </section>
      )}

      <section className="snapshot-band">
        <div className="snapshot-grid" data-testid="course-snapshot">
          {p.snapshot.map(([label, value], i) => (
            <div className="snapshot-item" key={label} data-testid={`snapshot-${i + 1}`}><span>{label}</span><strong>{value}</strong></div>
          ))}
        </div>
      </section>

      <section className="section-pad-slim">
        <div className="eyebrow"><span />Schedule of skills</div>
        <h2 className="course-h2">What will you actually learn?</h2>
        <p className="course-sub">{p.competencies.length} core competencies</p>
        <ol className="competency-grid">
          {p.competencies.map((c, i) => <li key={c} data-testid={`competency-${i + 1}`}><span>{String(i + 1).padStart(2, "0")}</span>{c}</li>)}
        </ol>
      </section>

      <section className="section-pad-slim curriculum-section">
        <div className="eyebrow gold"><span />Case file register</div>
        <h2 className="course-h2">{p.curriculumTitle || "The 6-month curriculum roadmap"}</h2>
        <div className="curriculum-list">
          {p.curriculum.map((m, i) => (
            <article className="month-card" key={m.month} data-testid={`curriculum-month-${i + 1}`}>
              <div className="month-head"><span className="month-num">{m.month}</span><h3>{m.title}</h3></div>
              <div className="month-groups">
                {m.groups.map(g => (
                  <div className="month-group" key={g.label}>
                    <strong>{g.label}</strong>
                    <ul>{g.items.map(item => <li key={item}>{item}</li>)}</ul>
                  </div>
                ))}
              </div>
              {m.pf && <div className="pf-badge" data-testid={`pf-badge-${i + 1}`}><FileText size={13} /> {m.pf}</div>}
            </article>
          ))}
        </div>
      </section>

      <section className="portfolio-band">
        <div className="portfolio-inner">
          <div className="eyebrow gold"><span />Evidence of work</div>
          <h2 className="course-h2 light">Your practice portfolio & scorecard</h2>
          <div className="portfolio-stats">
            {p.portfolio.stats.map(([num, label], i) => <div key={label} data-testid={`portfolio-stat-${i + 1}`}><strong>{num}</strong><span>{label}</span></div>)}
          </div>
          <div className="scorecard">
            <div className="scorecard-title"><ClipboardList size={15} /> Practice readiness scorecard</div>
            <div className="scorecard-grid">{p.portfolio.scorecard.map((s, i) => <span key={s} data-testid={`scorecard-${i + 1}`}>{s}<b>/10</b></span>)}</div>
          </div>
          <p className="completion-note"><Award size={16} /> {p.completion}</p>
        </div>
      </section>

      <section className="section-pad-slim">
        <div className="eyebrow"><span />Standing resources</div>
        <h2 className="course-h2">The ADVOKIT {p.toolkitName || (p.key === "diploma" ? "Junior Lawyer" : "Corporate Law")} Toolkit</h2>
        <div className="toolkit-grid">
          {p.toolkit.map(([name, items], i) => (
            <div className="toolkit-card" key={name} data-testid={`toolkit-${i + 1}`}>
              <strong>{name}</strong>
              <ul>{items.map(item => <li key={item}>{item}</li>)}</ul>
            </div>
          ))}
        </div>
      </section>

      {p.outcomes && (
        <section className="section-pad-slim">
          <div className="eyebrow"><span />Outcomes</div>
          <h2 className="course-h2">What you walk away with</h2>
          <ul className="outcome-list">{p.outcomes.map((o, i) => <li key={o} data-testid={`outcome-${i + 1}`}><BookOpenCheck size={15} /> {o}</li>)}</ul>
        </section>
      )}

      {p.faqs && (
        <section className="section-pad-slim">
          <div className="eyebrow"><span />Questions students ask</div>
          <h2 className="course-h2">Frequently asked questions</h2>
          <div className="faq-list">
            {p.faqs.map((f, i) => {
              const item = typeof f === "string" ? { q: f } : f;
              return (
                <div className="faq-item" key={item.q} data-testid={`faq-${i + 1}`}>
                  <div className="faq-q"><span>Q{i + 1}</span>{item.q}</div>
                  {item.a && <p className="faq-a">{item.a}</p>}
                </div>
              );
            })}
          </div>
          <p className="faq-note">For answers to any of these, <a data-testid="faq-whatsapp-link" href={enrol} target="_blank" rel="noreferrer">chat with the ADVOKIT team on WhatsApp</a>.</p>
        </section>
      )}

      <section className="course-final-cta">
        <h2>Ready to become <em>practice-ready?</em></h2>
        <p>Seats are limited to {p.batchSize} participants per batch. Message ADVOKIT for current batches, fees and enrolment guidance.</p>
        <div className="course-hero-actions center">
          <a className="whatsapp-btn" data-testid="course-final-enrol-button" href={enrol} target="_blank" rel="noreferrer">Enrol via WhatsApp <MessageCircle size={17} /></a>
          <a className="brochure-btn" data-testid="course-final-brochure-button" href={p.brochure} target="_blank" rel="noreferrer"><Download size={16} /> Download brochure</a>
        </div>
        <div><Link to={other.path} className="other-course" data-testid="other-course-link">Also explore: {other.title} <ArrowUpRight size={15} /></Link></div>
      </section>
    </main>
  );
}
